/**
 * Extraction de texte locale depuis PDF, Word et Images
 * Tout se passe côté client - pas d'API externe
 */

import mammoth from "mammoth";
import type JSZip from "jszip";

// Type pour le résultat d'extraction
export interface ExtractionResult {
  text: string;
  lines: string[];
  names: DetectedName[];
}

export interface DetectedName {
  fullName: string;
  firstName?: string;
  lastName?: string;
  line: string;
  lineNumber: number;
  confidence: "high" | "medium" | "low";
}

// Préfixes de noms de famille (à combiner avec le mot suivant)
const NAME_PREFIXES = new Set([
  "di", "de", "da", "del", "della", "dello", "dei", "degli",
  "van", "von", "den", "der", "ter", "ten",
  "le", "la", "los", "las", "du", "des",
  "mc", "mac", "al", "ben", "ibn", "el",
]);

// Liste de prénoms courants pour améliorer la détection
const FRENCH_FIRST_NAMES = new Set([
  // Masculins français
  "jean", "pierre", "michel", "andre", "philippe", "alain", "bernard", "jacques",
  "daniel", "roger", "robert", "paul", "christian", "eric", "laurent", "patrick",
  "nicolas", "francois", "david", "thomas", "christophe", "julien", "sebastien",
  "alexandre", "guillaume", "olivier", "vincent", "maxime", "antoine", "kevin",
  "lucas", "hugo", "mathieu", "clement", "florian", "romain", "jeremy", "damien",
  "louis", "gabriel", "raphael", "arthur", "leo", "nathan", "ethan", "noah",
  "lucca", "luca", "theo", "enzo", "adam", "jules", "victor", "martin",
  // Féminins français
  "marie", "jeanne", "francoise", "monique", "catherine", "nathalie", "isabelle",
  "sylvie", "christine", "valerie", "sandrine", "sophie", "stephanie", "veronique",
  "anne", "claire", "julie", "emilie", "laura", "marine", "pauline", "camille",
  "lea", "léa", "manon", "chloe", "sarah", "emma", "charlotte", "alice", "louise",
  "lucie", "mathilde", "juliette", "clemence", "oceane", "jade", "lola", "ines",
  "anna", "eva", "lily", "rose", "zoe", "nina", "clara", "elena", "victoria",
  "megane", "mégane", "caroline", "audrey", "laetitia", "laëtitia", "aurelie",
  // Prénoms internationaux courants
  "taylan", "mehmet", "mustafa", "ali", "fatima", "ayse", "emine", "ahmed",
  "mohamed", "mohammed", "youssef", "omar", "hassan", "karim", "said", "rachid",
  "fatou", "aminata", "mamadou", "ibrahima", "abdoulaye", "oumar", "aissatou",
  "maria", "jose", "carlos", "antonio", "manuel", "pedro", "paulo", "joao",
  "manon", "marion", "margot", "maeva", "mael", "yanis", "ilyes", "rayan",
]);

// Patterns pour détecter les noms
const NAME_PATTERNS = [
  /(?:nom\s*(?:et\s*prénom)?|patient|patiente?)\s*[:\-]?\s*([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+(?:\s+[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+)+)/gi,
  /(?:prénom\s*(?:et\s*nom)?)\s*[:\-]?\s*([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+(?:\s+[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+)+)/gi,
  /(?:m\.|mme|mr|mlle|dr|docteur)\s+([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+(?:\s+[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+)*)/gi,
  // NOM1 NOM2 Prénom (nom de famille composé : BELLEC POULNOIS Noeline) — avant NOM Prénom pour priorité
  /([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ]{2,}(?:\s+[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ]{2,})+)\s+([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+)/g,
  /([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+)\s+([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ]{2,})/g, // Prénom NOM
  /([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ]{2,})\s+([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+)/g, // NOM Prénom
  // Noms composés avec préfixes (DI, DE, VAN, etc.)
  /\b(DI|DE|DA|DEL|VAN|VON|LE|LA)\s+([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ]{2,})\s+([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+)/gi,
];

// Mots à exclure (pas des noms)
const EXCLUDED_WORDS = new Set([
  "les", "un", "une", "et", "ou", "mais", "donc",
  "car", "ni", "que", "qui", "quoi", "dont", "où", "ce", "cette", "ces", "mon",
  "ton", "son", "notre", "votre", "leur", "dans", "sur", "sous", "avec", "sans",
  "pour", "par", "en", "entre", "vers", "chez", "depuis", "pendant", "avant",
  "après", "contre", "malgré", "selon", "janvier", "février", "mars", "avril",
  "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre",
  "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche",
  "bilan", "orthoptique", "orthoptiste", "consultation", "examen", "patient", "date", "né",
  "née", "ans", "mois", "jour", "page", "total", "résultat", "test", "vision",
  "oeil", "yeux", "droit", "gauche", "acuité", "visuelle", "correction",
  // Titres à exclure
  "dr", "docteur", "mlle", "prof", "professeur", "maître",
  "dernier", "centre", "médical", "cabinet",
]);

/**
 * Extrait le texte d'un fichier Word (.docx), y compris le texte des images via OCR
 */
export async function extractFromWord(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  
  // Extraire le texte du document
  const result = await mammoth.extractRawText({ arrayBuffer });
  let text = result.value;
  
  // Extraire les images du document et faire de l'OCR
  try {
    // Les fichiers .docx sont des archives ZIP
    const JSZip = await import("jszip");
    const zip = await JSZip.default.loadAsync(arrayBuffer);
    
    // Les images sont stockées dans word/media/
    const imageEntries: { name: string; file: JSZip.JSZipObject }[] = [];
    zip.forEach((relativePath, file) => {
      if (relativePath.startsWith("word/media/") && !file.dir) {
        // Détecter les types d'images courants
        const ext = relativePath.toLowerCase();
        if (ext.endsWith(".png") || ext.endsWith(".jpg") || ext.endsWith(".jpeg") || ext.endsWith(".gif") || ext.endsWith(".bmp")) {
          imageEntries.push({ name: relativePath, file });
        }
      }
    });
    
    if (imageEntries.length > 0) {
      // Charger Tesseract pour l'OCR
      const Tesseract = await import("tesseract.js");
      
      // Traiter chaque image avec OCR
      const ocrPromises = imageEntries.map(async ({ name, file }) => {
        try {
          // Convertir le fichier ZIP en Blob pour Tesseract
          const blob = await file.async("blob");
          const mimeType = name.endsWith(".png") ? "image/png" : name.endsWith(".jpg") || name.endsWith(".jpeg") ? "image/jpeg" : name.endsWith(".gif") ? "image/gif" : name.endsWith(".bmp") ? "image/bmp" : "image/png";
          const imageFile = new File([blob], name.split("/").pop() || "image.png", { type: mimeType });
          
          // Faire l'OCR sur l'image
          const ocrResult = await Tesseract.recognize(imageFile, "fra", {
            logger: () => {
              // Log silencieux pour éviter le spam en console
            },
          });
          
          const ocrText = ocrResult.data.text.trim();
          return ocrText;
        } catch (ocrError) {
          console.warn(`Erreur OCR sur l'image ${name} du document Word:`, ocrError);
          return "";
        }
      });
      
      const ocrTexts = await Promise.all(ocrPromises);
      const combinedOcrText = ocrTexts.filter(Boolean).join("\n\n");
      
      // Ajouter le texte OCR au texte principal
      if (combinedOcrText) {
        text += "\n\n--- Texte extrait des images (OCR) ---\n\n" + combinedOcrText;
      }
    }
  } catch (imagesError) {
    // Si l'extraction d'images échoue, on continue avec juste le texte
    console.warn("Impossible d'extraire les images du document Word:", imagesError);
  }
  
  return text;
}

/**
 * Extrait le texte d'un fichier Word ancien (.doc)
 * Utilise une API route côté serveur car le format .doc est binaire
 */
export async function extractFromDoc(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch("/api/extract-doc", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Erreur lors de l'extraction du fichier .doc");
  }

  const data = await response.json();
  return data.text;
}

/**
 * Extrait le texte d'un fichier PDF
 */
export async function extractFromPDF(file: File): Promise<string> {
  // Import dynamique pour éviter les problèmes SSR
  const pdfjsLib = await import("pdfjs-dist");

  // Worker local (évite l'échec du CDN cdnjs)
  pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
  
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  
  let fullText = "";
  
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items
      .map((item: any) => item.str)
      .join(" ");
    fullText += pageText + "\n";
  }
  
  return fullText;
}

/**
 * Extrait le texte d'une image via OCR (Tesseract.js)
 */
export async function extractFromImage(file: File): Promise<string> {
  const Tesseract = await import("tesseract.js");
  
  const result = await Tesseract.recognize(file, "fra", {
    logger: (m) => console.log(m),
  });
  
  return result.data.text;
}

/**
 * Détecte les noms et prénoms dans le texte
 */
export function detectNames(text: string, lines: string[]): DetectedName[] {
  const detectedNames: DetectedName[] = [];
  const seenNames = new Set<string>();
  
  lines.forEach((line, index) => {
    // Ignorer les lignes trop courtes
    if (line.trim().length < 3) return;
    
    // Chercher avec les patterns
    for (const pattern of NAME_PATTERNS) {
      const regex = new RegExp(pattern.source, pattern.flags);
      let match;
      
      while ((match = regex.exec(line)) !== null) {
        let fullName = "";
        let firstName = "";
        let lastName = "";
        
        // Si on a deux groupes capturés (NOM Prénom ou Prénom NOM)
        if (match[1] && match[2]) {
          const part1 = match[1].trim();
          const part2 = match[2].trim();
          
          // Déterminer qui est le prénom et qui est le nom
          const isFirstAllCaps = part1 === part1.toUpperCase() && part1.length > 1;
          const isSecondAllCaps = part2 === part2.toUpperCase() && part2.length > 1;
          const isFirstKnownName = FRENCH_FIRST_NAMES.has(part1.toLowerCase());
          const isSecondKnownName = FRENCH_FIRST_NAMES.has(part2.toLowerCase());
          
          if (isFirstAllCaps && !isSecondAllCaps) {
            // Format: NOM Prénom (ex: AKYOL Taylan)
            lastName = part1;
            firstName = part2;
            fullName = `${part1} ${part2}`;
          } else if (!isFirstAllCaps && isSecondAllCaps) {
            // Format: Prénom NOM (ex: Taylan AKYOL)
            firstName = part1;
            lastName = part2;
            fullName = `${part1} ${part2}`;
          } else if (isSecondKnownName) {
            // Le second est un prénom connu -> NOM Prénom
            lastName = part1;
            firstName = part2;
            fullName = `${part1} ${part2}`;
          } else {
            // Par défaut: Prénom Nom
            firstName = part1;
            lastName = part2;
            fullName = `${part1} ${part2}`;
          }
        } else {
          fullName = match[1]?.trim() || match[0]?.trim();
          if (!fullName) continue;
          const parts = fullName.split(/\s+/);
          if (parts.length < 2) continue;
          firstName = parts[0];
          lastName = parts.slice(1).join(" ");
        }
        
        if (!fullName || seenNames.has(fullName.toLowerCase())) continue;
        
        // Vérifier que ce ne sont pas des mots exclus
        const allParts = [firstName, lastName].filter(Boolean);
        const isExcluded = allParts.some(p => EXCLUDED_WORDS.has(p.toLowerCase()));
        if (isExcluded) continue;
        
        // Calculer la confiance
        const isKnownFirstName = FRENCH_FIRST_NAMES.has(firstName.toLowerCase());
        const hasUpperLastName = lastName === lastName.toUpperCase();
        
        let confidence: "high" | "medium" | "low" = "low";
        if (isKnownFirstName && hasUpperLastName) confidence = "high";
        else if (isKnownFirstName || hasUpperLastName) confidence = "medium";
        
        seenNames.add(fullName.toLowerCase());
        detectedNames.push({
          fullName,
          firstName,
          lastName,
          line: line.trim(),
          lineNumber: index + 1,
          confidence,
        });
      }
    }
    
    // Chercher les séquences de mots avec majuscules (heuristique)
    const words = line.split(/\s+/);
    
    // D'abord chercher les noms en 3 mots avec préfixe (DI MASSO Lea, VAN DEN BERG Pierre, etc.)
    for (let i = 0; i < words.length - 2; i++) {
      const word1 = words[i];
      const word2 = words[i + 1];
      const word3 = words[i + 2];
      
      const cleanWord1 = word1.replace(/[^a-zA-ZàâäéèêëïîôùûüÿœæçÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ'-]/g, "");
      const cleanWord2 = word2.replace(/[^a-zA-ZàâäéèêëïîôùûüÿœæçÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ'-]/g, "");
      const cleanWord3 = word3.replace(/[^a-zA-ZàâäéèêëïîôùûüÿœæçÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ'-]/g, "");
      
      // Vérifier si word1 est un préfixe de nom (DI, DE, VAN, etc.)
      if (NAME_PREFIXES.has(cleanWord1.toLowerCase()) && cleanWord2.length >= 2 && cleanWord3.length >= 2) {
        const isWord2AllCaps = cleanWord2 === cleanWord2.toUpperCase();
        const isWord3KnownFirstName = FRENCH_FIRST_NAMES.has(cleanWord3.toLowerCase());
        const startsWithCap3 = /^[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ]/.test(cleanWord3);
        
        // Pattern: PRÉFIXE NOM Prénom (ex: DI MASSO Lea)
        if (isWord2AllCaps && startsWithCap3 && !cleanWord3.match(/^[A-Z]+$/)) {
          const lastName = `${cleanWord1.toUpperCase()} ${cleanWord2}`;
          const firstName = cleanWord3;
          const fullName = `${lastName} ${firstName}`;
          
          if (!seenNames.has(fullName.toLowerCase()) && !EXCLUDED_WORDS.has(cleanWord3.toLowerCase())) {
            seenNames.add(fullName.toLowerCase());
            
            const confidence = isWord3KnownFirstName ? "high" : "medium";
            
            detectedNames.push({
              fullName,
              firstName,
              lastName,
              line: line.trim(),
              lineNumber: index + 1,
              confidence,
            });
          }
        }
      }
    }
    
    // Noms en 3 mots : NOM1 NOM2 Prénom (ex. BELLEC POULNOIS Noeline)
    for (let i = 0; i < words.length - 2; i++) {
      const w1 = words[i].replace(/[^a-zA-ZàâäéèêëïîôùûüÿœæçÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ'-]/g, "");
      const w2 = words[i + 1].replace(/[^a-zA-ZàâäéèêëïîôùûüÿœæçÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ'-]/g, "");
      const w3 = words[i + 2].replace(/[^a-zA-ZàâäéèêëïîôùûüÿœæçÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ'-]/g, "");
      if (w1.length < 2 || w2.length < 2 || w3.length < 2) continue;
      if (EXCLUDED_WORDS.has(w1.toLowerCase()) || EXCLUDED_WORDS.has(w2.toLowerCase()) || EXCLUDED_WORDS.has(w3.toLowerCase())) continue;
      const isW1Caps = w1 === w1.toUpperCase() && w1.length > 1;
      const isW2Caps = w2 === w2.toUpperCase() && w2.length > 1;
      const isW3First = /^[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ]/.test(w3) && w3 !== w3.toUpperCase();
      const isW3Known = FRENCH_FIRST_NAMES.has(w3.toLowerCase());
      if (isW1Caps && isW2Caps && (isW3First || isW3Known)) {
        const lastName = `${w1} ${w2}`;
        const firstName = w3;
        const fullName = `${w1} ${w2} ${w3}`;
        if (seenNames.has(fullName.toLowerCase())) continue;
        seenNames.add(fullName.toLowerCase());
        const confidence = isW3Known ? "high" : "medium";
        detectedNames.push({
          fullName,
          firstName,
          lastName,
          line: line.trim(),
          lineNumber: index + 1,
          confidence,
        });
      }
    }

    // Ensuite chercher les noms en 2 mots
    for (let i = 0; i < words.length - 1; i++) {
      const word1 = words[i];
      const word2 = words[i + 1];
      
      const cleanWord1 = word1.replace(/[^a-zA-ZàâäéèêëïîôùûüÿœæçÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ'-]/g, "");
      const cleanWord2 = word2.replace(/[^a-zA-ZàâäéèêëïîôùûüÿœæçÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ'-]/g, "");
      
      if (cleanWord1.length < 2 || cleanWord2.length < 2) continue;
      if (EXCLUDED_WORDS.has(cleanWord1.toLowerCase())) continue;
      if (EXCLUDED_WORDS.has(cleanWord2.toLowerCase())) continue;
      
      // Vérifier si c'est un pattern NOM Prénom ou Prénom NOM
      const isWord1AllCaps = cleanWord1 === cleanWord1.toUpperCase();
      const isWord2AllCaps = cleanWord2 === cleanWord2.toUpperCase();
      const startsWithCap1 = /^[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ]/.test(cleanWord1);
      const startsWithCap2 = /^[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ]/.test(cleanWord2);
      
      if (!startsWithCap1 || !startsWithCap2) continue;
      
      let firstName = "";
      let lastName = "";
      let fullName = "";
      
      const isWord1KnownFirstName = FRENCH_FIRST_NAMES.has(cleanWord1.toLowerCase());
      const isWord2KnownFirstName = FRENCH_FIRST_NAMES.has(cleanWord2.toLowerCase());
      
      if (isWord1AllCaps && !isWord2AllCaps) {
        // NOM Prénom
        lastName = cleanWord1;
        firstName = cleanWord2;
        fullName = `${cleanWord1} ${cleanWord2}`;
      } else if (!isWord1AllCaps && isWord2AllCaps) {
        // Prénom NOM
        firstName = cleanWord1;
        lastName = cleanWord2;
        fullName = `${cleanWord1} ${cleanWord2}`;
      } else if (isWord2KnownFirstName && !isWord1KnownFirstName) {
        // Le second est un prénom connu
        lastName = cleanWord1;
        firstName = cleanWord2;
        fullName = `${cleanWord1} ${cleanWord2}`;
      } else if (isWord1KnownFirstName && !isWord2KnownFirstName) {
        // Le premier est un prénom connu
        firstName = cleanWord1;
        lastName = cleanWord2;
        fullName = `${cleanWord1} ${cleanWord2}`;
      } else {
        continue;
      }
      
      if (seenNames.has(fullName.toLowerCase())) continue;
      
      // Calculer la confiance
      const isKnownFirstName = FRENCH_FIRST_NAMES.has(firstName.toLowerCase());
      const hasUpperLastName = lastName === lastName.toUpperCase();
      
      let confidence: "high" | "medium" | "low" = "low";
      if (isKnownFirstName && hasUpperLastName) confidence = "high";
      else if (isKnownFirstName || hasUpperLastName) confidence = "medium";
      
      seenNames.add(fullName.toLowerCase());
      detectedNames.push({
        fullName,
        firstName,
        lastName,
        line: line.trim(),
        lineNumber: index + 1,
        confidence,
      });
    }
  });
  
  // Trier par confiance puis par numéro de ligne
  return detectedNames.sort((a, b) => {
    const confidenceOrder = { high: 0, medium: 1, low: 2 };
    const confDiff = confidenceOrder[a.confidence] - confidenceOrder[b.confidence];
    if (confDiff !== 0) return confDiff;
    return a.lineNumber - b.lineNumber;
  });
}

/**
 * Fonction principale d'extraction
 */
export async function extractTextFromFile(file: File): Promise<ExtractionResult> {
  const fileName = file.name.toLowerCase();
  let text = "";
  
  if (file.type.startsWith("image/")) {
    text = await extractFromImage(file);
  } else if (fileName.endsWith(".pdf")) {
    text = await extractFromPDF(file);
  } else if (fileName.endsWith(".docx")) {
    text = await extractFromWord(file);
  } else if (fileName.endsWith(".doc")) {
    text = await extractFromDoc(file);
  } else {
    throw new Error("Format de fichier non supporté");
  }
  
  const lines = text.split("\n").map(l => l.trim()).filter(l => l.length > 0);
  const names = detectNames(text, lines);
  
  return {
    text,
    lines,
    names,
  };
}
